import {
	MenuItemVisibilityField as BaseMenuItemVisibilityField,
	BlockSingleSelectField,
	CheckboxField,
	CreateDrawerFormProps,
	DateTimeField,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	MenuItemTypeSelectField,
	SingleSelectField,
	Text,
	TextField,
	TimeControlledPreviewTypeField,
	UpdateDrawerFormProps,
	defaultVisibilityOptions,
	isExternalUrl,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { isPast, parseISO } from "date-fns";
import { FormikErrors, useFormikContext } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { menuItemService } from "../shared/api/menuItemService";
import {
	MenuItemFormValues,
	MenuItemTranslationEntry,
	MenuItemUpdateResource,
	initialMenuItemTypes,
} from "../shared/types";

const Spaced = styled.div`
	padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

const pages = [
	"/events",
	"/events/upcoming",
	"/events/past",
	"/profile",
	"/profile/details",
	"/profile/preferences",
	"/profile/connected-accounts",
	"/profile/authenticated-sessions",
];

const useValidate = () => {
	const { t } = useTranslation();

	return (values: MenuItemFormValues) => {
		const errors: FormikErrors<MenuItemFormValues> = {};
		if (values.type !== "PAGE" && values.type !== "CATEGORY" && values.url === "") {
			errors.url = t("Field is required");
		}

		if (
			values.url &&
			values.type !== "CATEGORY" &&
			values.type !== "PAGE" &&
			values.type !== "INTERNAL" &&
			!isExternalUrl(values.url)
		) {
			errors.url = t("Invalid URL format");
		}

		if (values.type === "PAGE" && !values.block) {
			errors.block = t("Field is required");
		}
		if (values.isTimeControlled) {
			if (!values.previewUntil) {
				errors.previewUntil = t("Field is required");
			}

			if (values.previewUntil !== null && isPast(parseISO(values.previewUntil))) {
				errors.previewUntil = t("Date and time must be in the future");
			}

			if (
				values.previewUrl &&
				values.previewType !== "PAGE" &&
				values.previewType !== "INTERNAL" &&
				!isExternalUrl(values.previewUrl)
			) {
				errors.previewUrl = t("Invalid URL format");
			}
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, translation) => {
				const errors: FormikErrors<MenuItemTranslationEntry> = {};
				if (!translation.name) {
					errors.name = t("Field is required");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation: MenuItemTranslationEntry = { name: "" };

const MenuItemDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<MenuItemFormValues>();
	const menuItemTypes = initialMenuItemTypes;

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField label={t("Label")} name="name" required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<MenuItemTypeSelectField menuItemTypes={menuItemTypes} required />
				{values.type === "PAGE" && (
					<BlockSingleSelectField name="block" label={t("Block")} blockType="pageBlock" clearable />
				)}
				{values.type === "INTERNAL" && (
					<Spaced>
						<CheckboxField label={t("Predefined Link")} name="isPredefinedLink" />
					</Spaced>
				)}
				{values.type !== "PAGE" &&
					(values.type !== "INTERNAL" || !values.isPredefinedLink) &&
					values.type !== "CATEGORY" && <TextField label={t("Link")} name="url" required />}
				{values.type === "INTERNAL" && values.isPredefinedLink && (
					<SingleSelectField
						name="url"
						label={t("Link")}
						options={pages}
						getLabel={(e) => e}
						renderOption={(e) => <Text>{e}</Text>}
						required
						clearable
					/>
				)}
				<BaseMenuItemVisibilityField
					options={[...defaultVisibilityOptions]}
					name="visibility"
					label={t("Visibility")}
					required
				/>
				<CheckboxField name="isTimeControlled" label={t("Time controlled content visibility")} />
				{values.isTimeControlled && (
					<>
						<DateTimeField name="previewUntil" label={t("Preview until")} />
						<TimeControlledPreviewTypeField menuItemTypes={menuItemTypes} />
						{values.previewType === "INTERNAL" && (
							<Spaced>
								<CheckboxField label={t("Predefined Link")} name="isPreviewPredefinedLink" />
							</Spaced>
						)}
						{values.previewType === "PAGE" && (
							<BlockSingleSelectField
								name="previewBlock"
								label={t("Preview Block")}
								blockType="pageBlock"
								clearable={true}
							/>
						)}
						{values.previewType === "INTERNAL" && values.isPreviewPredefinedLink && (
							<SingleSelectField
								name="previewUrl"
								label={t("Preview URL")}
								options={pages}
								getLabel={(e) => e}
								renderOption={(e) => <Text>{e}</Text>}
								required
								clearable
							/>
						)}
						{!(values.previewType === "INTERNAL" && values.isPreviewPredefinedLink) &&
							!(values.previewType === "PAGE") && <TextField name="previewUrl" label={t("Preview URL")} required />}
					</>
				)}
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const MenuItemCreateDrawer: FC<CreateDrawerFormProps<MenuItemFormValues>> = (drawerProps) => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();
	const validate = useValidate();

	const initialValue: MenuItemFormValues = useMemo(() => {
		return {
			previewType: null,
			previewUntil: null,
			previewUrl: "",
			visibility: "all",
			type: "INTERNAL",
			url: "",
			previewBlock: null,
			block: null,
			groups: [],
			children: [],
			isTimeControlled: false,
			isPreviewPredefinedLink: true,
			isPredefinedLink: true,
			translations: [{ locale: languageSettings.fallback, ...emptyTranslation }],
		};
	}, [languageSettings.fallback]);

	return (
		<I18nFormDrawer
			{...drawerProps}
			validate={validate}
			title={t("Create menu item")}
			initialValues={initialValue}
			renderForm={(props) => <MenuItemDrawerContent {...props} />}
		/>
	);
};

const mapToFormValues = (resource: MenuItemUpdateResource): MenuItemFormValues => {
	return {
		type: resource.type,
		previewUntil: resource.previewUntil,
		children: resource.children,
		url: resource.url,
		isPredefinedLink: resource.type === "INTERNAL" && pages.includes(resource.url),
		block: resource.block,
		previewType: resource.previewType,
		previewUrl: resource.previewUrl,
		isPreviewPredefinedLink: resource.previewType === "INTERNAL" && pages.includes(resource.previewUrl ?? ""),
		isTimeControlled: resource.previewUntil !== null,
		previewBlock: resource.previewBlock,
		visibility: resource.visibility,
		translations: resource.translations,
	};
};

export const MenuItemUpdateDrawer: FC<UpdateDrawerFormProps<MenuItemFormValues>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...drawerProps}
			validate={validate}
			queryFn={() => menuItemService.show(drawerProps.id).then(({ data }) => mapToFormValues(data.data))}
			queryKey={menuItemService.showKey(drawerProps.id)}
			title={t("Edit menu item")}
			renderSuccess={(props) => <MenuItemDrawerContent {...props} />}
		/>
	);
};
